import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, MenuItem,
    FormControl, InputLabel, Select, CircularProgress, 
    Stack, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, Paper, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Snackbar, Alert, Chip, Tooltip, Zoom
} from '@mui/material';
import { 
    EditCalendarRounded, CloudSyncRounded, 
    ArrowBackIosNewRounded, ArrowForwardIosRounded, 
    CheckCircleRounded, InventoryRounded, CloseRounded
} from '@mui/icons-material';
import { doc, getDoc, collection, query, onSnapshot, addDoc, serverTimestamp, getDocs, where } from 'firebase/firestore';
import { auth, db } from '../../firebase';

// Premium Theme Palette
const THEME = {
    BG_DARK: "#06362D",
    PANEL_DARK: "#06362D",
    DEEP_DARK: "#04241E",
    BORDER_DARK: "#D1A505",
    TEXT_PRIMARY: "#DEE2B1",
    TEXT_SECONDARY: "#D1A505",
    ACCENT_GOLD: "#FFD500",
    STRICT_DARK_BORDER: "rgba(0, 0, 0, 0.5)",
    DANGER: "#EF4444",
    DANGER_BG: "rgba(239, 68, 68, 0.15)",
    SUCCESS: "#10B981",
    SUCCESS_BG: "rgba(16, 185, 129, 0.15)",
    WARNING: "#F59E0B",
    WARNING_BG: "rgba(245, 158, 11, 0.15)"
};

function InventoryUpdate() {
    const [loading, setLoading] = useState(true);
    const [isPushing, setIsPushing] = useState(false);
    const [propertyId, setPropertyId] = useState("");
    const [rooms, setRooms] = useState([]);
    const [updateHistory, setUpdateHistory] = useState([]);
    
    // Toast Notification State
    const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

    // Calendar Grid State
    const [calendarStartDate, setCalendarStartDate] = useState(new Date());
    const [pendingEdits, setPendingEdits] = useState({}); 
    const daysToShow = 14;

    // Bulk Update Modal State
    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [bulkRoomId, setBulkRoomId] = useState("");
    const [bulkStartDate, setBulkStartDate] = useState("");
    const [bulkEndDate, setBulkEndDate] = useState("");
    const [bulkCount, setBulkCount] = useState("");

    // --- Helpers ---
    const showToast = (message, severity = "error") => {
        setToast({ open: true, message, severity });
    };

    const handleCloseToast = (event, reason) => {
        if (reason === 'clickaway') return;
        setToast({ ...toast, open: false });
    };

    // --- Data Fetching ---
    useEffect(() => {
        const fetchUserAndRooms = async () => {
            const user = auth.currentUser;
            if (user) {
                const roleSnap = await getDoc(doc(db, "authroles", user.uid));
                if (roleSnap.exists()) {
                    const pId = roleSnap.data().propertyId;
                    setPropertyId(pId);

                    const invSnap = await getDoc(doc(db, "ota_room_inventory", pId));
                    if (invSnap.exists()) setRooms(invSnap.data().rooms || []);
                }
            }
            setLoading(false);
        };
        fetchUserAndRooms();
    }, []);

    // Listen to successfully synced updates to render directly in the calendar
    useEffect(() => {
        if (!propertyId) return;
        const q = query(collection(db, "ota_inventory_updates"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const updates = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(item => item.propertyId === propertyId && item.status === "Synced");
            
            const sortedUpdates = updates.sort((a, b) => {
                const dateA = a.syncedAt ? new Date(a.syncedAt).getTime() : 0;
                const dateB = b.syncedAt ? new Date(b.syncedAt).getTime() : 0;
                return dateB - dateA;
            });

            setUpdateHistory(sortedUpdates);
        });
        return () => unsubscribe();
    }, [propertyId]);

    // --- Calendar Logic ---
    const getFormatDateStr = (dateObj) => {
        const d = new Date(dateObj);
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().split('T')[0];
    };

    const getCalendarDates = () => {
        const dates = [];
        for (let i = 0; i < daysToShow; i++) {
            const d = new Date(calendarStartDate);
            d.setDate(d.getDate() + i);
            dates.push(d);
        }
        return dates;
    };

    const shiftCalendar = (days) => {
        const newDate = new Date(calendarStartDate);
        newDate.setDate(newDate.getDate() + days);
        setCalendarStartDate(newDate);
    };

    const getCellCurrentValue = (roomId, dateStr) => {
        const overlappingUpdate = updateHistory.find(update => 
            String(update.roomId) === String(roomId) && 
            dateStr >= update.startDate && 
            dateStr <= update.endDate
        );
        if (overlappingUpdate) {
            return { value: String(overlappingUpdate.availability), isSynced: true };
        }
        return { value: "", isSynced: false };
    };

    const handleCellEdit = (roomId, dateStr, value) => {
        const key = `${roomId}_${dateStr}`;
        if (value === "") {
            const newEdits = { ...pendingEdits };
            delete newEdits[key];
            setPendingEdits(newEdits);
        } else {
            setPendingEdits(prev => ({ ...prev, [key]: value }));
        }
    };

    // --- Live Validation ---
    const validateRoomCount = async (roomId, countToApply) => {
        try {
            const q = query(
                collection(db, "rooms"),
                where("propertyId", "==", propertyId),
                where("cmId", "==", String(roomId))
            );
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
                const roomDoc = querySnapshot.docs[0].data();
                const maxAllowed = roomDoc.maxCmRoomCount !== undefined ? Number(roomDoc.maxCmRoomCount) : Infinity;
                
                if (Number(countToApply) > maxAllowed) {
                    return { valid: false, maxAllowed, roomName: roomDoc.roomTypeName || roomId };
                }
            }
            return { valid: true };
        } catch (error) {
            console.error("Validation database error:", error);
            return { valid: true }; 
        }
    };

    // --- Core API Push Function ---
    const pushToChannelManager = async (dataToSync) => {
        try {
            const configSnap = await getDoc(doc(db, "channel_manager", propertyId));
            if (!configSnap.exists()) throw new Error("Channel Manager credentials not found.");
            
            const { hotelCode, authCode } = configSnap.data();

            const payload = {
                Authentication: { HotelCode: hotelCode, AuthCode: authCode },
                Inventories: {
                    Inventory: dataToSync.map(item => ({
                        RoomId: String(item.roomId),
                        FromDate: item.startDate,
                        ToDate: item.endDate,
                        AvailableRoomCount: String(item.availability)
                    }))
                }
            };

            const response = await fetch('https://navajowhite-wren-495848.hostingersite.com/api/inventory-update', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (result.APIResponse?.IsSuccess) return true;
            throw new Error(result.APIResponse?.Message || "API rejected the push.");
        } catch (err) {
            console.error(err);
            showToast(`Sync Failed: ${err.message}`, "error");
            return false;
        }
    };

    // --- Direct Push: Grid Edits ---
    const handlePushInlineEdits = async () => {
        const editKeys = Object.keys(pendingEdits);
        if (editKeys.length === 0) return;

        setIsPushing(true);

        for (const key of editKeys) {
            if (pendingEdits[key] === "") continue;
            
            const [roomId, dateStr] = key.split('_');
            const countToApply = Number(pendingEdits[key]);
            
            const validation = await validateRoomCount(roomId, countToApply);
            if (!validation.valid) {
                showToast(`Limit Exceeded: Cannot set ${validation.roomName} to ${countToApply} on ${dateStr}. Max is ${validation.maxAllowed}.`, "error");
                setIsPushing(false);
                return;
            }
        }

        const dataToSync = editKeys
            .filter(key => pendingEdits[key] !== "") 
            .map(key => {
                const [roomId, dateStr] = key.split('_');
                const roomName = rooms.find(r => String(r.RoomId) === String(roomId))?.RoomName || "Unknown";
                return { 
                    roomId: String(roomId), 
                    roomName, 
                    startDate: dateStr, 
                    endDate: dateStr, 
                    availability: String(pendingEdits[key]) 
                };
            });

        if (dataToSync.length === 0) {
            setIsPushing(false);
            return;
        }

        const isSuccess = await pushToChannelManager(dataToSync);
        
        if (isSuccess) {
            try {
                const promises = dataToSync.map(item => addDoc(collection(db, "ota_inventory_updates"), {
                    propertyId, 
                    roomId: item.roomId, 
                    roomName: item.roomName, 
                    availability: item.availability, 
                    startDate: item.startDate, 
                    endDate: item.endDate, 
                    status: "Synced", 
                    syncedAt: new Date().toISOString(), 
                    createdAt: serverTimestamp()
                }));
                await Promise.all(promises);
                setPendingEdits({}); 
                showToast("Grid updates pushed successfully!", "success");
            } catch (err) {
                console.error("Firestore logging failed", err);
            }
        }
        setIsPushing(false);
    };

    // --- Direct Push: Bulk Update ---
    const handlePushBulkUpdate = async () => {
        if (!bulkRoomId || !bulkStartDate || !bulkEndDate || !bulkCount) {
            return showToast("Please fill all fields for bulk update.", "warning");
        }

        setIsPushing(true);
        const countToApply = Number(bulkCount);
        const roomsToUpdate = bulkRoomId === "ALL" ? rooms : rooms.filter(r => String(r.RoomId) === String(bulkRoomId));
        
        for (const r of roomsToUpdate) {
            const validation = await validateRoomCount(r.RoomId, countToApply);
            if (!validation.valid) {
                showToast(`Limit Exceeded: Cannot set ${validation.roomName} to ${countToApply}. Max allowed is ${validation.maxAllowed}.`, "error");
                setIsPushing(false);
                return; 
            }
        }

        const dataToSync = roomsToUpdate.map(r => ({
            roomId: String(r.RoomId),
            roomName: r.RoomName,
            startDate: bulkStartDate,
            endDate: bulkEndDate,
            availability: String(bulkCount)
        }));

        const isSuccess = await pushToChannelManager(dataToSync);

        if (isSuccess) {
            try {
                const promises = dataToSync.map(item => addDoc(collection(db, "ota_inventory_updates"), {
                    ...item, 
                    propertyId, 
                    status: "Synced", 
                    syncedAt: new Date().toISOString(), 
                    createdAt: serverTimestamp()
                }));
                await Promise.all(promises);
                setBulkModalOpen(false);
                setBulkRoomId(""); setBulkStartDate(""); setBulkEndDate(""); setBulkCount("");
                showToast("Bulk update pushed successfully!", "success");
            } catch (err) {
                console.error("Firestore logging failed", err);
            }
        }
        setIsPushing(false);
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: THEME.BG_DARK }}>
                <CircularProgress size={40} thickness={4} sx={{ color: THEME.ACCENT_GOLD }} />
            </Box>
        );
    }

    const calendarDates = getCalendarDates();
    const hasPendingEdits = Object.keys(pendingEdits).length > 0;

    return (
        <Box sx={{ display: 'flex', bgcolor: THEME.BG_DARK, minHeight: '100vh', color: THEME.TEXT_PRIMARY }}>
            <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 } }}>
                
                {/* Header Section */}
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 4 }}>
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
                            <Box sx={{ p: 1, bgcolor: THEME.DEEP_DARK, borderRadius: 2, color: THEME.ACCENT_GOLD, display: 'flex', border: `1px solid ${THEME.BORDER_DARK}` }}>
                                <InventoryRounded />
                            </Box>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: THEME.ACCENT_GOLD, letterSpacing: '-0.02em' }}>
                                Live Inventory
                            </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ color: THEME.TEXT_PRIMARY, opacity: 0.85, ml: 6 }}>
                            Manage and sync your availability directly to the channel manager.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <Button 
                            variant="outlined" 
                            disableElevation
                            sx={{ 
                                borderColor: THEME.BORDER_DARK, 
                                color: THEME.TEXT_PRIMARY, 
                                fontWeight: 700, 
                                borderRadius: 2,
                                bgcolor: THEME.DEEP_DARK,
                                '&:hover': { borderColor: THEME.ACCENT_GOLD, bgcolor: THEME.PANEL_DARK, color: THEME.ACCENT_GOLD }, 
                                px: 3
                            }}
                            startIcon={<EditCalendarRounded sx={{ color: THEME.BORDER_DARK }} />} 
                            onClick={() => setBulkModalOpen(true)}
                        >
                            Bulk Update
                        </Button>
                        {hasPendingEdits && (
                            <Button 
                                variant="contained" 
                                disableElevation
                                sx={{ 
                                    bgcolor: THEME.ACCENT_GOLD, 
                                    color: THEME.DEEP_DARK, 
                                    fontWeight: 800, 
                                    borderRadius: 2, 
                                    px: 3,
                                    boxShadow: `0 4px 14px 0 rgba(255, 213, 0, 0.35)`,
                                    '&:hover': { bgcolor: THEME.BORDER_DARK, color: '#FFFFFF' }
                                }}
                                startIcon={isPushing ? <CircularProgress size={16} sx={{ color: THEME.DEEP_DARK }} /> : <CloudSyncRounded sx={{ color: THEME.DEEP_DARK }} />} 
                                onClick={handlePushInlineEdits}
                                disabled={isPushing}
                            >
                                Push Edits ({Object.keys(pendingEdits).length})
                            </Button>
                        )}
                    </Stack>
                </Stack>

                {/* Legend */}
                <Stack direction="row" spacing={2} sx={{ mb: 2.5, px: 1 }}>
                    <Chip 
                        size="small" 
                        icon={<CheckCircleRounded sx={{ color: `${THEME.SUCCESS} !important` }} />} 
                        label="Synced Live" 
                        sx={{ bgcolor: THEME.SUCCESS_BG, color: THEME.SUCCESS, fontWeight: 700, border: `1px solid ${THEME.SUCCESS}` }} 
                    />
                    <Chip 
                        size="small" 
                        icon={<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: THEME.ACCENT_GOLD, ml: 1 }} />} 
                        label="Unsaved Edit" 
                        sx={{ bgcolor: THEME.WARNING_BG, color: THEME.ACCENT_GOLD, fontWeight: 700, border: `1px solid ${THEME.BORDER_DARK}` }} 
                    />
                </Stack>

                {/* Calendar Grid */}
                <TableContainer 
                    component={Paper} 
                    elevation={0} 
                    sx={{ 
                        borderRadius: 3, 
                        border: `1px solid ${THEME.BORDER_DARK}`, 
                        bgcolor: THEME.PANEL_DARK,
                        overflow: 'hidden', 
                        boxShadow: `0px 4px 24px ${THEME.STRICT_DARK_BORDER}` 
                    }}
                >
                    {/* Controls Bar */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: THEME.DEEP_DARK, borderBottom: `1px solid ${THEME.BORDER_DARK}` }}>
                        <IconButton 
                            onClick={() => shiftCalendar(-7)} 
                            size="small" 
                            sx={{ color: THEME.ACCENT_GOLD, bgcolor: THEME.PANEL_DARK, border: `1px solid ${THEME.BORDER_DARK}`, '&:hover': { bgcolor: THEME.BG_DARK } }}
                        >
                            <ArrowBackIosNewRounded fontSize="small" sx={{ fontSize: '1rem' }} />
                        </IconButton>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: THEME.TEXT_PRIMARY, letterSpacing: '0.02em' }}>
                            {calendarDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} 
                            &nbsp; — &nbsp;
                            {calendarDates[calendarDates.length - 1].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Typography>
                        <IconButton 
                            onClick={() => shiftCalendar(7)} 
                            size="small" 
                            sx={{ color: THEME.ACCENT_GOLD, bgcolor: THEME.PANEL_DARK, border: `1px solid ${THEME.BORDER_DARK}`, '&:hover': { bgcolor: THEME.BG_DARK } }}
                        >
                            <ArrowForwardIosRounded fontSize="small" sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </Box>

                    <Table size="small" sx={{ minWidth: 1000 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: THEME.DEEP_DARK }}>
                                <TableCell sx={{ minWidth: 180, color: THEME.ACCENT_GOLD, fontWeight: 800, borderRight: `1px solid ${THEME.BORDER_DARK}`, borderBottom: `1px solid ${THEME.BORDER_DARK}`, py: 2 }}>
                                    ROOM TYPE
                                </TableCell>
                                {calendarDates.map((date, idx) => (
                                    <TableCell key={idx} align="center" sx={{ minWidth: 60, borderRight: `1px solid ${THEME.BORDER_DARK}`, borderBottom: `1px solid ${THEME.BORDER_DARK}`, p: 1.5 }}>
                                        <Typography variant="caption" sx={{ display: 'block', color: THEME.TEXT_SECONDARY, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            {date.toLocaleDateString('en-US', { weekday: 'short' })}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 800, color: THEME.TEXT_PRIMARY, mt: 0.5 }}>
                                            {date.getDate()} <span style={{ fontSize: '0.75em', color: THEME.TEXT_SECONDARY }}>{date.toLocaleDateString('en-US', { month: 'short' })}</span>
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ bgcolor: THEME.PANEL_DARK }}>
                            {rooms.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={daysToShow + 1} align="center" sx={{ py: 6, color: THEME.TEXT_PRIMARY, borderBottom: 'none' }}>
                                        No rooms found. Please check your inventory mappings.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rooms.map((room, roomIdx) => {
                                    const isLastRoom = roomIdx === rooms.length - 1;
                                    return (
                                        <TableRow key={room.RoomId} sx={{ '&:hover': { bgcolor: `${THEME.DEEP_DARK}88` } }}>
                                            <TableCell sx={{ fontWeight: 700, color: THEME.TEXT_PRIMARY, borderRight: `1px solid ${THEME.BORDER_DARK}`, borderBottom: isLastRoom ? 'none' : `1px solid ${THEME.BORDER_DARK}`, py: 2 }}>
                                                {room.RoomName}
                                            </TableCell>
                                            {calendarDates.map((date, idx) => {
                                                const dateStr = getFormatDateStr(date);
                                                const key = `${room.RoomId}_${dateStr}`;
                                                
                                                const cellData = getCellCurrentValue(room.RoomId, dateStr);
                                                const pendingValue = pendingEdits[key];
                                                const isEdited = pendingValue !== undefined;
                                                
                                                const displayValue = isEdited ? pendingValue : cellData.value;
                                                const showSyncBadge = cellData.isSynced && !isEdited;

                                                return (
                                                    <TableCell 
                                                        key={idx} 
                                                        align="center" 
                                                        sx={{ 
                                                            p: 0, 
                                                            borderRight: `1px solid ${THEME.BORDER_DARK}`, 
                                                            borderBottom: isLastRoom ? 'none' : `1px solid ${THEME.BORDER_DARK}`,
                                                            bgcolor: isEdited ? THEME.WARNING_BG : 'transparent',
                                                            position: 'relative',
                                                            transition: 'background-color 0.2s',
                                                            '&:hover': { bgcolor: isEdited ? `${THEME.ACCENT_GOLD}33` : `${THEME.DEEP_DARK}99` }
                                                        }}
                                                    >
                                                        <input
                                                            type="number"
                                                            value={displayValue}
                                                            onChange={(e) => handleCellEdit(room.RoomId, dateStr, e.target.value)}
                                                            placeholder="-"
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                minHeight: '56px',
                                                                border: 'none',
                                                                background: 'transparent',
                                                                textAlign: 'center',
                                                                fontWeight: isEdited ? 900 : 700,
                                                                fontSize: '15px',
                                                                color: isEdited ? THEME.ACCENT_GOLD : THEME.TEXT_PRIMARY,
                                                                outline: 'none',
                                                                cursor: 'text'
                                                            }}
                                                        />
                                                        {showSyncBadge && displayValue !== "" && (
                                                            <Tooltip title="Synced to Channel Manager" placement="top" arrow TransitionComponent={Zoom}>
                                                                <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
                                                                    <CheckCircleRounded sx={{ fontSize: 13, color: THEME.SUCCESS }} />
                                                                </Box>
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Bulk Update Modal */}
                <Dialog 
                    open={bulkModalOpen} 
                    onClose={() => !isPushing && setBulkModalOpen(false)} 
                    maxWidth="xs" 
                    fullWidth
                    PaperProps={{ 
                        sx: { 
                            borderRadius: 3, 
                            border: `1px solid ${THEME.BORDER_DARK}`,
                            bgcolor: THEME.DEEP_DARK,
                            boxShadow: `0 20px 25px -5px ${THEME.STRICT_DARK_BORDER}` 
                        } 
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 800, color: THEME.ACCENT_GOLD, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        Bulk Update Inventory
                        <IconButton size="small" onClick={() => setBulkModalOpen(false)} disabled={isPushing} sx={{ color: THEME.TEXT_SECONDARY }}>
                            <CloseRounded fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ mt: 1 }}>
                        <Stack spacing={3}>
                            <FormControl fullWidth size="medium">
                                <InputLabel sx={{ color: THEME.TEXT_SECONDARY, '&.Mui-focused': { color: THEME.ACCENT_GOLD } }}>Select Room</InputLabel>
                                <Select 
                                    value={bulkRoomId} 
                                    label="Select Room" 
                                    onChange={(e) => setBulkRoomId(e.target.value)} 
                                    sx={{ 
                                        borderRadius: 2,
                                        color: THEME.TEXT_PRIMARY,
                                        bgcolor: THEME.PANEL_DARK,
                                        '.MuiOutlinedInput-notchedOutline': { borderColor: THEME.BORDER_DARK },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD },
                                        '.MuiSvgIcon-root': { color: THEME.ACCENT_GOLD }
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: THEME.DEEP_DARK,
                                                color: THEME.TEXT_PRIMARY,
                                                border: `1px solid ${THEME.BORDER_DARK}`
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value="ALL" sx={{ fontWeight: 800, color: THEME.ACCENT_GOLD }}>Update All Rooms</MenuItem>
                                    {rooms.map(r => (
                                        <MenuItem key={r.RoomId} value={String(r.RoomId)} sx={{ color: THEME.TEXT_PRIMARY, '&:hover': { bgcolor: THEME.PANEL_DARK } }}>
                                            {r.RoomName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Stack direction="row" spacing={2}>
                                <TextField 
                                    fullWidth 
                                    size="medium" 
                                    type="date" 
                                    label="Start Date" 
                                    InputLabelProps={{ shrink: true, sx: { color: THEME.TEXT_SECONDARY, '&.Mui-focused': { color: THEME.ACCENT_GOLD } } }} 
                                    value={bulkStartDate} 
                                    onChange={e => setBulkStartDate(e.target.value)} 
                                    InputProps={{ 
                                        sx: { 
                                            borderRadius: 2, 
                                            color: THEME.TEXT_PRIMARY, 
                                            bgcolor: THEME.PANEL_DARK,
                                            '.MuiOutlinedInput-notchedOutline': { borderColor: THEME.BORDER_DARK },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD }
                                        } 
                                    }} 
                                />
                                <TextField 
                                    fullWidth 
                                    size="medium" 
                                    type="date" 
                                    label="End Date" 
                                    InputLabelProps={{ shrink: true, sx: { color: THEME.TEXT_SECONDARY, '&.Mui-focused': { color: THEME.ACCENT_GOLD } } }} 
                                    value={bulkEndDate} 
                                    onChange={e => setBulkEndDate(e.target.value)} 
                                    InputProps={{ 
                                        sx: { 
                                            borderRadius: 2, 
                                            color: THEME.TEXT_PRIMARY, 
                                            bgcolor: THEME.PANEL_DARK,
                                            '.MuiOutlinedInput-notchedOutline': { borderColor: THEME.BORDER_DARK },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD }
                                        } 
                                    }} 
                                />
                            </Stack>
                            <TextField 
                                fullWidth 
                                size="medium" 
                                label="Available Count" 
                                type="number" 
                                value={bulkCount} 
                                onChange={e => setBulkCount(e.target.value)}
                                InputLabelProps={{ sx: { color: THEME.TEXT_SECONDARY, '&.Mui-focused': { color: THEME.ACCENT_GOLD } } }}
                                InputProps={{ 
                                    sx: { 
                                        borderRadius: 2, 
                                        color: THEME.TEXT_PRIMARY, 
                                        bgcolor: THEME.PANEL_DARK,
                                        '.MuiOutlinedInput-notchedOutline': { borderColor: THEME.BORDER_DARK },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME.ACCENT_GOLD }
                                    } 
                                }} 
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 1, borderTop: `1px solid ${THEME.BORDER_DARK}33` }}>
                        <Button 
                            onClick={() => setBulkModalOpen(false)} 
                            disabled={isPushing} 
                            sx={{ color: THEME.TEXT_SECONDARY, fontWeight: 700 }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="contained" 
                            disableElevation
                            sx={{ 
                                bgcolor: THEME.ACCENT_GOLD, 
                                color: THEME.DEEP_DARK, 
                                fontWeight: 800, 
                                borderRadius: 2, 
                                px: 3, 
                                '&:hover': { bgcolor: THEME.BORDER_DARK, color: '#FFFFFF' } 
                            }}
                            startIcon={isPushing ? <CircularProgress size={16} sx={{ color: THEME.DEEP_DARK }} /> : <CloudSyncRounded sx={{ color: THEME.DEEP_DARK }} />} 
                            onClick={handlePushBulkUpdate}
                            disabled={isPushing}
                        >
                            {isPushing ? "Syncing..." : "Push Live"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Global Toast / Snackbar */}
                <Snackbar
                    open={toast.open}
                    autoHideDuration={5000}
                    onClose={handleCloseToast}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert 
                        onClose={handleCloseToast} 
                        severity={toast.severity} 
                        sx={{ 
                            width: '100%', 
                            fontWeight: 700, 
                            borderRadius: 2, 
                            bgcolor: THEME.DEEP_DARK,
                            color: THEME.TEXT_PRIMARY,
                            border: `1px solid ${THEME.BORDER_DARK}`,
                            boxShadow: `0 10px 20px -3px ${THEME.STRICT_DARK_BORDER}`,
                            '& .MuiAlert-icon': {
                                color: toast.severity === 'error' ? THEME.DANGER : THEME.ACCENT_GOLD
                            }
                        }}
                    >
                        {toast.message}
                    </Alert>
                </Snackbar>

            </Box>
        </Box>
    );
}

export default InventoryUpdate;